import { memo, useCallback } from 'react';

import { useTrackAgentThreadListOpened } from '@air/analytics';
import { ListTodoIcon, MessageSquareIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { WORKSPACE_AGENT_TASKS_TOGGLE_FOCUS_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentTasksToggleFocusAttribute';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type WorkspaceAgentPanelView } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelView';

const getTasksButtonTooltipLabel = ({
  isBackToChatDisabled,
  isTasksView,
}: {
  isBackToChatDisabled: boolean;
  isTasksView: boolean;
}) => {
  if (!isTasksView) {
    return 'View tasks';
  }

  return isBackToChatDisabled ? 'No chat selected' : 'Back to chat';
};

export type PrivateWorkspaceAgentPanelTasksToggleButtonProps = {
  disabled: boolean;
  panelView: WorkspaceAgentPanelView;
  onPanelViewChange: (nextView: WorkspaceAgentPanelView) => void;
};

export const PrivateWorkspaceAgentPanelTasksToggleButton = memo(
  ({ disabled, panelView, onPanelViewChange }: PrivateWorkspaceAgentPanelTasksToggleButtonProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const { trackAgentThreadListOpened } = useTrackAgentThreadListOpened();

    const isTasksView = panelView === 'tasks';
    const isBackToChatDisabled =
      isTasksView && !session.isLoadingSessions && session.sessionId === null && session.sessions.length === 0;
    const tooltipLabel = getTasksButtonTooltipLabel({ isBackToChatDisabled, isTasksView });
    const isButtonDisabled = disabled || isBackToChatDisabled;

    const handleClick = useCallback(() => {
      if (isBackToChatDisabled) {
        return;
      }

      if (!isTasksView) {
        trackAgentThreadListOpened({ location: 'thread' });
      }

      onPanelViewChange(isTasksView ? 'chat' : 'tasks');
    }, [isBackToChatDisabled, isTasksView, onPanelViewChange, trackAgentThreadListOpened]);

    return (
      <Tooltip label={tooltipLabel} side="bottom" sideOffset={4}>
        <IconButton
          appearance="ghost"
          aria-pressed={isTasksView}
          className={cn(
            'transition-none hover:transition-colors',
            isTasksView && !isButtonDisabled && 'ads:bg-subtle bg-gray-3 ads:text-icon-emphasis text-gray-12',
            isBackToChatDisabled && 'disabled:bg-transparent ads:disabled:text-icon-subtle disabled:text-gray-8',
          )}
          color="gray"
          data-testid="WORKSPACE_AGENT_TASKS_BUTTON"
          disabled={isButtonDisabled}
          {...{ [WORKSPACE_AGENT_TASKS_TOGGLE_FOCUS_ATTRIBUTE]: '' }}
          label={tooltipLabel}
          onClick={handleClick}
          size="small"
        >
          {isTasksView ? <MessageSquareIcon aria-hidden size="sm" /> : <ListTodoIcon aria-hidden size="sm" />}
        </IconButton>
      </Tooltip>
    );
  },
);

PrivateWorkspaceAgentPanelTasksToggleButton.displayName = 'PrivateWorkspaceAgentPanelTasksToggleButton';
